import { useState, useRef } from "react";
import { api, API, formatDate } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, Download, Loader2, Paperclip,
} from "lucide-react";

/**
 * Compact PDF-only attachments panel used on BathDetailPage.
 * Kept intentionally lightweight — the dashboard is untouched.
 *
 * Props:
 *  - recordId: string
 *  - documents: [{path, name, label, size, uploaded_at, uploaded_by}]
 *  - canEdit: boolean — show upload / delete controls
 *  - onChange: () => void — fired after a successful upload / delete
 */
export const DocumentsPanel = ({ recordId, documents = [], canEdit, onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("Proof of delivery");
  const inputRef = useRef(null);

  const pick = () => inputRef.current?.click();

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Only PDF documents are allowed");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      toast.error("PDF too large (max 20MB)");
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", (label || "").trim());
      await api.post(`/bath-records/${recordId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} attached`);
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const download = async (idx, doc) => {
    try {
      const token = localStorage.getItem("wbt_token");
      const r = await fetch(`${API}/bath-records/${recordId}/documents/${idx}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.name || "document.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error("Download failed");
    }
  };

  const remove = async (idx, doc) => {
    if (!window.confirm(`Remove "${doc.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/bath-records/${recordId}/documents/${idx}`);
      toast.success("Document removed");
      onChange && onChange();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    }
  };

  const fmtSize = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  return (
    <div data-testid="documents-panel">
      {documents.length === 0 && !canEdit && (
        <p className="text-[11px] text-zinc-400 italic">No documents attached.</p>
      )}

      {documents.length > 0 && (
        <ul className="space-y-1.5 mb-3">
          {documents.map((d, idx) => (
            <li
              key={idx}
              className="flex items-center gap-2 bg-zinc-50 border border-zinc-200 rounded-md px-2.5 py-2 hover:border-zinc-300"
              data-testid={`document-row-${idx}`}
            >
              <FileText className="h-4 w-4 text-rose-600 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  {d.label && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-700 bg-zinc-200 rounded px-1.5 py-0.5">
                      {d.label}
                    </span>
                  )}
                  <span className="text-xs font-semibold text-zinc-900 truncate" title={d.name}>
                    {d.name}
                  </span>
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5 truncate">
                  {fmtSize(d.size)} · {d.uploaded_by || "—"} · {formatDate(d.uploaded_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => download(idx, d)}
                className="p-1.5 rounded hover:bg-zinc-200 text-zinc-700"
                title="Download"
                data-testid={`document-download-${idx}`}
              >
                <Download className="h-3.5 w-3.5" />
              </button>
              {canEdit && (
                <button
                  type="button"
                  onClick={() => remove(idx, d)}
                  className="p-1.5 rounded hover:bg-rose-50 text-rose-600"
                  title="Remove"
                  data-testid={`document-remove-${idx}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && (
        <div className="border-t border-dashed border-zinc-200 pt-3">
          <div className="flex items-center gap-2 mb-2">
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Label (e.g. Proof of delivery)"
              className="h-8 text-xs"
              maxLength={80}
              data-testid="document-label-input"
            />
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={upload}
            data-testid="document-file-input"
          />
          <Button
            type="button"
            size="sm"
            onClick={pick}
            disabled={uploading}
            className="w-full bg-zinc-950 hover:bg-zinc-800 text-white h-8 text-xs"
            data-testid="document-upload-button"
          >
            {uploading ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Uploading…</>
            ) : (
              <><Upload className="h-3.5 w-3.5 mr-1.5" /> Attach PDF</>
            )}
          </Button>
          <p className="text-[10px] text-zinc-400 mt-1.5 flex items-center gap-1">
            <Paperclip className="h-2.5 w-2.5" />
            PDF only · max 20MB · e.g. UPN proof of delivery, delivery notes, invoices
          </p>
        </div>
      )}
    </div>
  );
};
